/* eslint-disable @typescript-eslint/no-unused-vars */
import { sortedUniq } from 'lodash-es';
import AnalystData from '../data/analyst';
import { logger } from '../util/logger';
import { post, HttpResponse } from './request';

type ValueOf<T> = T[keyof T];

export enum ECmsId {
  Directors = 'DIRECTORS',
  Governance = 'GOVERNANCE',
  Announcement = 'ANNOUNCEMENT',
  Result = 'RESULT',
  Report = 'REPORT',
  CsrReport = 'CSRREPORT',
  Calendar = 'CALENDAR',
  ConferenceCalls = 'CONFERENCECALLS',
}

type CmsId = ValueOf<typeof ECmsId>;

// 投资者关系页面数据 key 枚举
export enum EInvestorSectionKey {
  CorporateGovernance = 'corporate-governance',
  Announcements = 'announcements',
  QuarterlyResults = 'quarterly-results',
  FinancialReports = 'financial-reports',
  CsrReports = 'csr-reports',
  EventCalendar = 'event-calendar',
  ConferenceCalls = 'conference-calls',
  AnalystCoverage = 'analyst-coverage',
  StockInformation = 'stock-information',
  ContactUs = 'contact-us',
}

type Lang = 'zh-CN' | 'zh-HK' | 'en-US';

interface IDocResponse {
  cmsid: number;
  clientid: number;
  yyyy: string;
  mm: string;
  dd: string;
  status: string;
  approved: string;
  name: string;
  title: string;
  description: string;
  headline: string;
  'sub-headline': string;
  link: string;
  attachment: string;
  image: string;
  cover: string;
  id: number;
}

export interface IDirector {
  name: string;
  title: string;
  description: string;
}

export interface IDoc {
  id: number;
  title: string;
  subTitle: string;
  coverImage: string;
  link: string;
  year: string;
  date: string;
}

export interface IAnalyst {
  name: string;
  coop: string;
  email: string;
}

export interface IInvestorRelationData {
  [EInvestorSectionKey.CorporateGovernance]: {
    directors: IDirector[];
    docs: IDoc[];
  };
  [EInvestorSectionKey.Announcements]: IDoc[];
  [EInvestorSectionKey.QuarterlyResults]: IDoc[];
  [EInvestorSectionKey.FinancialReports]: IDoc[];
  [EInvestorSectionKey.CsrReports]: IDoc[];
  [EInvestorSectionKey.EventCalendar]: IDoc[];
  [EInvestorSectionKey.ConferenceCalls]: {
    title: string;
    description: string;
  };
  [EInvestorSectionKey.AnalystCoverage]: IAnalyst[];
}

interface IMetaDocResp {
  media_url: string;
  data: IDocResponse[];
}

const transformToDocs = (resp: string) => {
  try {
    const meta = JSON.parse(resp) as unknown as IMetaDocResp;
    const { data, media_url: mediaUrl } = meta;

    return data?.map((d) => {
      return {
        id: d.id,
        title: d.headline || '',
        subTitle: d['sub-headline'] || '',
        coverImage: d.cover ? mediaUrl + d.cover : '',
        link: mediaUrl + (d.attachment || d.link || d.image) || '',
        year: d.yyyy || '',
        date: `${d.yyyy}-${d.mm}-${d.dd}`,
      };
    });
  } catch (e) {
    logger.error(e);
    return [];
  }
};

const transformToDirectors = (resp: string) => {
  try {
    const meta = JSON.parse(resp) as unknown as IMetaDocResp;
    const { data } = meta;

    return data?.map((d) => {
      return {
        name: d.name,
        title: d.title,
        description: d.description,
      };
    });
  } catch (e) {
    logger.error(e);
    return [];
  }
};

const transformToConferenceCalls = (resp: string) => {
  try {
    const meta = JSON.parse(resp) as unknown as IMetaDocResp;
    const { data } = meta;

    return {
      title: data?.[0]?.headline,
      description: data?.[0]?.description,
    };
  } catch (e) {
    logger.error(e);
    return {
      title: '',
      description: '',
    };
  }
};

function getIRPageData(lanType: Lang): Promise<IInvestorRelationData> {
  return post<
    { lanType: Lang },
    HttpResponse<{
      [key in CmsId]: string;
    }>
  >('/smart/view/investor/r/tInvestorService_getInvestorData', {
    lanType,
  }).then((resp) => {
    const { data } = resp;

    const pageData = {
      [EInvestorSectionKey.CorporateGovernance]: {
        directors: transformToDirectors(data.DIRECTORS),
        docs: transformToDocs(data.GOVERNANCE),
      },
      [EInvestorSectionKey.Announcements]: transformToDocs(data.ANNOUNCEMENT),
      [EInvestorSectionKey.QuarterlyResults]: transformToDocs(data.RESULT),
      [EInvestorSectionKey.FinancialReports]: transformToDocs(data.REPORT),
      [EInvestorSectionKey.CsrReports]: transformToDocs(data.CSRREPORT),
      [EInvestorSectionKey.EventCalendar]: transformToDocs(data.CALENDAR),
      [EInvestorSectionKey.ConferenceCalls]: transformToConferenceCalls(data.CONFERENCECALLS),
      [EInvestorSectionKey.AnalystCoverage]: AnalystData as IAnalyst[],
    };

    return pageData;
  });
}

interface IDocListParams {
  cmsId: ECmsId;
  locale: 'zh-CN' | 'zh-HK' | 'en-US';
}

export interface IDocListData {
  years: string[];
  docs: IDoc[];
}

function searchDocList(params: IDocListParams): Promise<IDocListData> {
  return post<
    {
      ecmsName: string;
      lanType: string;
    },
    HttpResponse<string>
  >('/smart/view/investor/r/tInvestorService_getInvestorDetail', {
    ecmsName: params.cmsId,
    lanType: params.locale,
  }).then((resp) => {
    const { data } = resp;
    const docs = transformToDocs(data);

    return {
      years: sortedUniq(docs.map((doc) => doc.year)),
      docs,
    };
  });
}

export { getIRPageData, searchDocList };
